import { useCallback, useState } from 'react';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { ENDORSEMENTS_EDIT_HASH } from 'src/app/shared/constants';

type UseEndorsementFlowProps = {
	petitionSlug: string;
};

type UseEndorsementFlowReturn = ModelHookResult<
	{
		isHelpModalOpen: boolean;
		isSubmitModalOpen: boolean;
		isTermsAgreed: boolean;
	},
	{
		handleBack: () => void;
		handleHelpClick: () => void;
		openHelpModal: () => void;
		closeHelpModal: () => void;
		openSubmitModal: () => void;
		closeSubmitModal: () => void;
		navigateToDashboard: () => void;
		navigateToEditPage: () => void;
		handleTermsChange: (checked: boolean) => void;
	}
>;

export function useEndorsementFlow({ petitionSlug }: UseEndorsementFlowProps): UseEndorsementFlowReturn {
	const navigate = useNavigate();
	const track = useTracking();
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
	const [isTermsAgreed, setIsTermsAgreed] = useState(false);

	const navigateToDashboard = useCallback(() => {
		navigate(`/p/${petitionSlug}/dashboard`);
	}, [navigate, petitionSlug]);

	const navigateToEditPage = useCallback(() => {
		navigate(`/p/${petitionSlug}/edit?endorsementAdded=true${ENDORSEMENTS_EDIT_HASH}`);
	}, [navigate, petitionSlug]);

	const handleBack = useCallback(() => {
		navigateToDashboard();
	}, [navigateToDashboard]);

	const handleHelpClick = useCallback(() => {
		void track('petition_manage_endorsements_example_click');
		setIsHelpModalOpen(true);
	}, [track]);

	const openHelpModal = useCallback(() => setIsHelpModalOpen(true), []);
	const closeHelpModal = useCallback(() => setIsHelpModalOpen(false), []);
	const openSubmitModal = useCallback(() => setIsSubmitModalOpen(true), []);
	const closeSubmitModal = useCallback(() => {
		setIsSubmitModalOpen(false);
		setIsTermsAgreed(false);
	}, []);
	const handleTermsChange = useCallback((checked: boolean) => setIsTermsAgreed(checked), []);

	return {
		data: {
			isHelpModalOpen,
			isSubmitModalOpen,
			isTermsAgreed,
		},
		actions: {
			handleBack,
			handleHelpClick,
			openHelpModal,
			closeHelpModal,
			openSubmitModal,
			closeSubmitModal,
			navigateToDashboard,
			navigateToEditPage,
			handleTermsChange,
		},
	};
}
